import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PageLayout } from '@/components';
import {
  ClaimStatusTag,
  EvidenceGallery,
  ClaimTimeline,
} from '@/components/claims';
import { useFetchClaimByUid } from '@/hooksApi/useClaim';
import { formatIQD, formatClaimDeadline } from '@/utils/claimHelpers';
import { formatDateByDayjs } from '@/utils';
import { ROUTE } from '@/config';
import { PermissionName } from '@/types';
import usePermission from '@/hooks/usePermission';
import { message } from 'antd';
import {
  ArrowLeft,
  Copy,
  ShieldAlert,
  PackageX,
  AlertTriangle,
  FileText,
  ClipboardList,
  Clock,
  ImageIcon,
  ExternalLink,
} from 'lucide-react';

/* ─── Section Header (matches order detail SectionHeader) ─── */
function SectionHeader({ icon: Icon, title }: { icon: typeof FileText; title: string }) {
  return (
    <div className='flex items-center gap-2 mb-4'>
      <Icon className='h-4 w-4 text-sd-muted-foreground' />
      <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
        {title}
      </span>
    </div>
  );
}

/* ─── Claim type badge (matches order type badge) ─── */
function ClaimTypeInlineBadge({ type, t }: { type: string; t: (k: string) => string }) {
  const config: Record<string, { labelKey: string; icon: JSX.Element }> = {
    damaged: { labelKey: 'claim_type_damaged', icon: <PackageX size={12} /> },
    partially_damaged: { labelKey: 'claim_type_partially_damaged', icon: <ShieldAlert size={12} /> },
    lost: { labelKey: 'claim_type_lost', icon: <AlertTriangle size={12} /> },
  };
  const c = config[type] ?? { labelKey: type, icon: <ShieldAlert size={12} /> };
  return (
    <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-solid bg-white' style={{ borderColor: 'rgb(232,230,227)' }}>
      {c.icon}
      {String(t(c.labelKey))}
    </span>
  );
}

const ClaimDetails = () => {
  const { uid } = useParams<{ uid: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  // Boxy Select (spec §7.2): no carrier identity on merchant-facing claims.

  const { data: response, isFetching } = useFetchClaimByUid(uid ?? '');
  const claim = response?.object;

  if (!hasPermission(PermissionName.VIEW_CLAIMS)) {
    return (
      <PageLayout>
        <div className='flex items-center justify-center min-h-[300px]'>
          <span className='text-sm text-[rgb(115,123,140)]'>
            {String(t('no_permission', { defaultValue: 'No permission' }))}
          </span>
        </div>
      </PageLayout>
    );
  }

  if (isFetching) {
    return (
      <PageLayout>
        <div className='flex items-center justify-center min-h-[300px]'>
          <div className='h-8 w-8 rounded-full border-2 border-[rgb(237,224,74)] border-t-transparent animate-spin' />
        </div>
      </PageLayout>
    );
  }

  if (!claim) {
    return (
      <PageLayout>
        <div className='flex items-center justify-center min-h-[300px]'>
          <span className='text-sm text-[rgb(115,123,140)]'>
            {String(t('claim_not_found', { defaultValue: 'Claim not found.' }))}
          </span>
        </div>
      </PageLayout>
    );
  }

  const isRejected = claim.status.slug === 'rejected';
  const isCompensated = claim.status.slug === 'compensated';
  const isCompensationFailed = claim.status.slug === 'compensation-failed';

  const handleCopy = () => {
    navigator.clipboard.writeText(claim.platform_code);
    message.success(String(t('copied')));
  };

  const summaryRows: { label: string; value: string }[] = [
    { label: String(t('declared_value', { defaultValue: 'Declared Value' })), value: formatIQD(claim.declared_value) },
    {
      label: String(t('compensation', { defaultValue: 'Compensation' })),
      value: claim.compensation_amount !== null ? formatIQD(claim.compensation_amount) : String(t('pending', { defaultValue: 'Pending' })),
    },
    { label: String(t('filed_by', { defaultValue: 'Filed By' })), value: String(t(`claim_filed_by.${claim.filed_by_type}`, { defaultValue: claim.filed_by_type })) },
    { label: String(t('claim_deadline', { defaultValue: 'Claim Deadline' })), value: formatClaimDeadline(claim.claim_window_deadline, t) },
    {
      label: String(t('reviewed_at', { defaultValue: 'Reviewed At' })),
      value: claim.reviewed_at ? formatDateByDayjs(claim.reviewed_at, 'DD MMM YYYY HH:mm') : '—',
    },
  ];

  return (
    <PageLayout>
          {/* ─── Sub-header: Code + Date + Status ─── */}
          <div className='mb-5'>
            <div className='flex items-start justify-between gap-3'>
              <div>
                <div className='flex items-center gap-2'>
                  <span className='text-xl font-bold tracking-tight'>{claim.platform_code}</span>
                  <button
                    onClick={handleCopy}
                    className='p-1 rounded-md hover:bg-sd-muted transition-colors cursor-pointer bg-transparent border-0 text-sd-muted-foreground hover:text-sd-foreground'
                  >
                    <Copy className='h-4 w-4' strokeWidth={2} />
                  </button>
                </div>
                <p className='text-[12px] text-sd-muted-foreground mt-1 m-0'>
                  {formatDateByDayjs(claim.created_at, 'D MMMM YYYY')}
                </p>
              </div>
              <div className='flex items-center gap-2 flex-shrink-0 mt-0.5'>
                <ClaimTypeInlineBadge type={claim.type} t={t} />
                <ClaimStatusTag slug={claim.status.slug} />
              </div>
            </div>
          </div>

          {/* ─── 2-Column Grid (matches order detail layout) ─── */}
          <div className='grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5 items-start'>

            {/* LEFT COLUMN */}
            <div className='flex flex-col gap-5'>

              {/* Order Reference */}
              <section className='rounded-2xl border border-sd-border bg-sd-card p-5'>
                <SectionHeader icon={FileText} title={String(t('order', { defaultValue: 'Order' }))} />
                <div className='flex items-center gap-2'>
                  <span
                    style={{ fontSize: '13px', fontWeight: 700, fontFamily: 'monospace', color: 'rgb(57,57,60)' }}
                  >
                    {claim.order.platform_code}
                  </span>
                  <button
                    onClick={() => window.open(`/orders/${claim.order.uid}`, '_blank')}
                    className='p-1 rounded-md hover:bg-sd-muted transition-colors cursor-pointer bg-transparent border-0 text-sd-muted-foreground hover:text-sd-foreground'
                    title={String(t('open_order', { defaultValue: 'Open order' }))}
                  >
                    <ExternalLink className='h-3.5 w-3.5' />
                  </button>
                </div>
              </section>

              {/* Description */}
              {claim.description && (
                <section className='rounded-2xl border border-sd-border bg-sd-card p-5'>
                  <SectionHeader icon={ClipboardList} title={String(t('claim_description_label', { defaultValue: 'Description' }))} />
                  <p className='text-[13px] leading-relaxed text-sd-foreground m-0'>
                    {claim.description}
                  </p>
                </section>
              )}

              {/* Evidence */}
              {claim.evidence.length > 0 && (
                <section className='rounded-2xl border border-sd-border bg-sd-card p-5'>
                  <SectionHeader icon={ImageIcon} title={String(t('evidence_photos', { defaultValue: 'Evidence' }))} />
                  <EvidenceGallery evidence={claim.evidence} />
                </section>
              )}

              {/* Rejection reason */}
              {isRejected && claim.rejection_reason && (
                <div className='rounded-2xl border border-solid border-[rgb(239,68,68)] bg-[rgb(254,242,242)] p-5'>
                  <span className='text-[13px] font-semibold text-[rgb(239,68,68)]'>
                    {String(t('rejection_reason', { defaultValue: 'Rejection Reason' }))}
                  </span>
                  <p className='text-[13px] text-[rgb(239,68,68)] mt-2 m-0'>{claim.rejection_reason}</p>
                </div>
              )}

              {/* Compensation outcome */}
              {(isCompensated || isCompensationFailed) && (
                <div className={`rounded-2xl border border-solid p-5 ${
                  isCompensated
                    ? 'border-[rgb(34,197,94)] bg-[rgb(240,253,244)]'
                    : 'border-[rgb(239,68,68)] bg-[rgb(254,242,242)]'
                }`}>
                  <span className={`text-[13px] font-semibold ${isCompensated ? 'text-[rgb(34,197,94)]' : 'text-[rgb(239,68,68)]'}`}>
                    {String(t('compensation_outcome', { defaultValue: 'Compensation Outcome' }))}
                  </span>
                  <div className='divide-y divide-sd-border mt-3'>
                    <div className='flex justify-between py-2.5 first:pt-0'>
                      <span className='text-[13px] text-sd-muted-foreground'>{String(t('declared_value'))}</span>
                      <span className='text-[13px] font-medium text-sd-foreground'>{formatIQD(claim.declared_value)}</span>
                    </div>
                    <div className='flex justify-between py-2.5 last:pb-0'>
                      <span className='text-[13px] text-sd-muted-foreground'>{String(t('compensation_amount'))}</span>
                      <span className={`text-[13px] font-semibold ${isCompensated ? 'text-[rgb(34,197,94)]' : 'text-[rgb(239,68,68)]'}`}>
                        {formatIQD(claim.compensation_amount)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN (sticky) */}
            <div className='flex flex-col gap-5 xl:sticky xl:top-0'>

              {/* Claim Summary */}
              <section className='rounded-2xl border border-sd-border bg-sd-card p-5'>
                <SectionHeader icon={ClipboardList} title={String(t('claim_summary', { defaultValue: 'Claim Summary' }))} />
                <div className='divide-y divide-sd-border'>
                  {summaryRows.map((row) => (
                    <div key={row.label} className='flex justify-between items-center py-2.5 first:pt-0 last:pb-0'>
                      <span className='text-[13px] text-sd-muted-foreground'>{row.label}</span>
                      <span className='text-[13px] font-medium text-sd-foreground text-end max-w-[55%]'>{row.value}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Claim Timeline */}
              {claim.statuses.length > 0 && (
                <section className='rounded-2xl border border-sd-border bg-sd-card p-5'>
                  <SectionHeader icon={Clock} title={String(t('claim_timeline', { defaultValue: 'Claim Timeline' }))} />
                  <ClaimTimeline statuses={claim.statuses} />
                </section>
              )}
            </div>
          </div>
    </PageLayout>
  );
};

export default ClaimDetails;
